import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

import AddressActions from 'app/shared/actions/AddressActions';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import getLocationRestrictionErrorMessage from 'app/shared/utils/getLocationRestrictionErrorMessageUtils';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import { PropertiesSelectors, UserSelectors } from 'app/shared/selectors';

const {
    GA_EVENTS: {
        EXISTING_LISTING_POSTED_WARNING,
    },
} = AnalyticsConstants;

const {
    PROPERTY_TYPES: {
        COMMUNITY,
        CONDO,
    },
    LISTING_TYPES: {
        RENTAL,
        ROOM,
    },
    LISTING_CODES_BY_TYPE,
    PROPERTY_CODES_BY_TYPE,
    PROPERTY_TYPES_BY_CODE,
} = PropertyConstants;

export const useLocationRestriction = ({
    isLoggedIn, updateDuplicateAccountListingId, updateErrorObj, updateIsLoading,
}) => {
    const [propertyTypeFromApi, updatePropertyTypeFromApi] = useState('');
    const dispatch = useDispatch();

    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const user = useSelector(state => UserSelectors.makeUser(state));


    async function checkLocationRestriction({
        address, unit, isRoomForRent, propertyType, hasAuthCookies,
    }) {
        updateIsLoading(true);
        const { listingAlias } = currentProperty;
        const brand = 'zillow';

        if (!address) {
            updateIsLoading(false);
            return false;
        }

        const getRestrictionsResponse = await ((isLoggedIn || hasAuthCookies) ? dispatch(
            AddressActions.getRestrictions({
                address,
                unit,
                listingTypeCode: isRoomForRent ? LISTING_CODES_BY_TYPE[ROOM] : LISTING_CODES_BY_TYPE[RENTAL],
                propertyTypeCode: propertyType ? PROPERTY_CODES_BY_TYPE[propertyType] : null,
            }),
        ) : dispatch(
            AddressActions.getPublicRestrictions({
                address,
                unit,
                listingTypeCode: isRoomForRent ? LISTING_CODES_BY_TYPE[ROOM] : LISTING_CODES_BY_TYPE[RENTAL],
                propertyTypeCode: propertyType ? PROPERTY_CODES_BY_TYPE[propertyType] : null,
            }),
        )).catch(() => {
            // call failed, do not update errorObj
            updateIsLoading(false);
        });

        const newErrorObj = {
            address: '',
            propertyType: '',
            numberOfUnits: '',
        };

        if (getRestrictionsResponse) {
            const errorMessage = getLocationRestrictionErrorMessage(getRestrictionsResponse, brand, user);

            if (errorMessage) {
                dispatch(analyticsUtils.track.GAEventV2({
                    ...EXISTING_LISTING_POSTED_WARNING,
                    ...(listingAlias) && { label: listingAlias },
                }));

                // if user sees the ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED message returned by
                // getLocationRestrictionErrorMessage(), then auto set the property to be community, unless
                // the user manually input a unit, which would make it a condo.
                if (!getRestrictionsResponse.willAppear) {
                    if (!propertyType) {
                        const propertyTypeToShow = unit ? CONDO : COMMUNITY;
                        updatePropertyTypeFromApi(propertyTypeToShow);
                    }

                    newErrorObj.propertyType = errorMessage;
                    updateErrorObj(newErrorObj);
                    updateIsLoading(false);
                    return false;
                }

                newErrorObj.address = errorMessage;
            }

            // only for logged in users
            if (getRestrictionsResponse.duplicateAccountListingId) {
                updateDuplicateAccountListingId(getRestrictionsResponse.duplicateAccountListingId);
            }
        }

        const getDetailsRes = await dispatch(
            AddressActions.getDetails({ address, unit: propertyType === CONDO ? unit : '' })
        ).catch((err) => {
            if (err) {
                if (isObject(err) && err.message) {
                    // Must specify an address and city, state, or zip
                    newErrorObj.address = err.message;
                }

                if (isString(err)) {
                    // Unable to match address
                    newErrorObj.address = err;
                }
            }
        });

        if (getDetailsRes && getDetailsRes.propertyTypeCode) {
            updatePropertyTypeFromApi(PROPERTY_TYPES_BY_CODE[getDetailsRes.propertyTypeCode]);
            newErrorObj.propertyType = '';
        }

        updateErrorObj(newErrorObj);
        updateIsLoading(false);

        const hasNoErrors = Object.values(newErrorObj).every(key => !key);
        return hasNoErrors;
    }

    return { checkLocationRestriction, propertyTypeFromApi };
};

export const useValidateForm = (updateErrorObj) => {
    const dispatch = useDispatch();

    async function verifyAddress({ address, unit, errorObj }) {
        try {
            const { isValidatedWithoutUnit } = await dispatch(AddressActions.verify({ address, unit }));

            // if the property is valid without a unit number
            if (unit !== '' && isValidatedWithoutUnit) {
                updateErrorObj({
                    ...errorObj, address: `We are unable to validate the
                    property address with this unit number. Please double-check before creating the property.`,
                });
            }

            return true;
        } catch (err) {
            if (err.message) {
                updateErrorObj({
                    ...errorObj, address: err.message,
                });
            }
            return false;
        }
    }

    const checkIsFormComplete = ({
        address, propertyType, numUnits, errorObj,
    }) => {
        const newErrorObj = {
            ...errorObj,
            address: address ? '' : 'Please enter an address',
            propertyType: propertyType ? '' : 'Please enter a property type',
            numberOfUnits: (!numUnits && propertyType === COMMUNITY) ? 'Please enter number of units' : '',
        };

        updateErrorObj(newErrorObj);

        return Object.values(newErrorObj).every(key => key === '');
    };

    return { checkIsFormComplete, verifyAddress };
};
